// Excel pivot filters + the "matches your Excel?" check for pinned tables (roadmap #188).
// PURE — no React, no network.
//
// REAL BUG this supports (a customer's WoW Media template): Excel pivots were filtered (3 of 8
// campaigns, one week of days, hidden rows) but the dashboard showed every row because the filter
// was silently dropped. The backend now applies those filters and describes them as
// `table_filters` (read-only chips) and, at import, recalculates each table from the uploaded
// data and compares it with the Excel values (`reconcile`). A difference is CONFIRMED by the
// customer (never a hard block); a filter Boomerang can't apply still blocks the table. Every
// customer-facing sentence comes from the backend (`table_pins._ISSUE_CATALOG`); the few
// fallbacks below mirror that wording for when the check request itself fails.

const SOURCE_RANK = { page: 0, hidden: 1 };

// The table's filter chips: backend `table_filters` (stored doc / dashboard visual) or
// `tableFilters` (resolve response). Page filters first, hidden-rows next, others after;
// unsupported (can't be applied) last. Stable within each rank.
export function chipsForTable(tableOrVisual) {
  const t = tableOrVisual || {};
  const list = Array.isArray(t.table_filters) ? t.table_filters
    : (Array.isArray(t.tableFilters) ? t.tableFilters : []);
  const rank = (c) => (c.status === "unsupported" ? 10 : 0) + (SOURCE_RANK[c.source] ?? 2);
  return list.map((c, i) => ({ c, i }))
    .sort((a, b) => (rank(a.c) - rank(b.c)) || (a.i - b.i))
    .map(({ c }) => c);
}

// The full values list for one chip (blank shown as "(blank)").
export function chipValues(chip) {
  const values = Array.isArray(chip && chip.values) ? chip.values.map(String) : [];
  return chip && chip.include_blank ? [...values, "(blank)"] : values;
}

// A chip expands to show its values when its summary can't list them all (more than 2), or to
// show WHY it can't be applied (unsupported).
export function chipExpandable(chip) {
  if (!chip) return false;
  if (chip.status === "unsupported") return Boolean(chip.reason_text);
  return chipValues(chip).length > 2;
}

// Fallback wording when the check request itself fails (mirrors RECONCILE_ERROR in the catalog).
export function reconcileRequestFailed(table) {
  const where = `${table.sheet}!${table.range}`;
  return {
    status: "error", requires_confirmation: true, groups: [], checked: 0, matched: 0,
    mismatch_count: 0, unchecked: 0,
    headline: `We couldn't check ${where} against your Excel right now`,
    reason_text: "The check against your Excel numbers didn't finish (the request failed).",
    action_text: "Try the check again, or confirm to publish this table without it.",
    confirm_label: "Publish this table without checking it against my Excel.",
  };
}

// Where one pinned table stands on the "matches your Excel?" check:
//   checking       — the check is running (publish waits)
//   ok             — matches / nothing to check / not checked (an unresolved table is gated by
//                    its own issues, not here)
//   needs_confirm  — values differ; the customer must confirm before publish
//   error          — the check couldn't run; confirm (or check again) before publish
//   confirmed      — a difference / failed check was explicitly confirmed
export function reconcileGate(table) {
  const t = table || {};
  if (t.reconcileChecking) return { state: "checking", canPublish: false };
  const r = t.reconcile;
  if (!r) return { state: "ok", canPublish: true };
  const needs = r.status === "error" || r.status === "mismatch" || r.requires_confirmation === true;
  if (!needs) return { state: "ok", canPublish: true };
  if (t.reconcile_ack === true) return { state: "confirmed", canPublish: true };
  return { state: r.status === "error" ? "error" : "needs_confirm", canPublish: false };
}

// The publish-step gate over every pinned table that can otherwise publish.
export function tablesReconcileGate(tables) {
  const relevant = (tables || []).filter((t) => t && t.canPublish);
  const states = relevant.map(reconcileGate);
  return {
    checking: states.some((s) => s.state === "checking"),
    needsConfirm: states.filter((s) => s.state === "needs_confirm" || s.state === "error").length,
    canPublish: states.every((s) => s.canPublish),
  };
}

// What the wizard sends back at publish for one table: the stored-doc fields plus ONLY the
// reconcile summary (the server gate reads `status`; the grouped cell list never travels back).
// Client-only check state (`reconcileChecking`, `reconcileToken`) never reaches the stored doc.
// fix-source-routing: the decided source travels as `source_category` (the entry the backend
// returned — re-validated server-side against the caller's own data) + `source_status`; the
// display-only `sourceOptions`/`sourceLabel` and the client-only `source_choice` stay behind.
export function tableForPublish(table) {
  const {
    reconcileChecking, reconcileToken, reconcile, sourceOptions, sourceLabel, source_choice,
    sourceCategory, sourceStatus, ...rest
  } = table || {};
  const entry = rest.source_category !== undefined ? rest.source_category : sourceCategory;
  const status = rest.source_status !== undefined ? rest.source_status : sourceStatus;
  if (entry !== undefined && entry !== null) rest.source_category = entry;
  else delete rest.source_category;
  if (status !== undefined && status !== null) rest.source_status = status;
  else delete rest.source_status;
  if (!reconcile) return rest;
  const { status: rstatus, checked, matched, mismatch_count, unchecked, checked_at, requires_confirmation } = reconcile;
  return { ...rest, reconcile: { status: rstatus, checked, matched, mismatch_count, unchecked, checked_at, requires_confirmation } };
}

// The one status line on a wizard table card. "✓ Ready to publish" ONLY once the table can
// publish AND its Excel check passed (or a difference was confirmed); while the check runs, or
// values differ / couldn't be calculated / the check failed, the table needs the customer's review.
export function tableStatusLine(table) {
  const t = table || {};
  const gate = reconcileGate(t);
  if (t.canPublish && (gate.state === "ok" || gate.state === "confirmed")) {
    return { tone: "ok", text: "✓ Ready to publish" };
  }
  return { tone: "review", text: "Needs your review before publishing" };
}

// A reconcile group's title: an uncalculable group says its own backend sentence (`text`); a
// mismatch group keeps "{column} (N cells)".
export function reconcileGroupTitle(group) {
  const g = group || {};
  if (g.kind === "uncalculable") return g.text || "";
  const n = Number(g.count) || 0;
  return `${g.column_header || ""} (${n} ${n === 1 ? "cell" : "cells"})`;
}

// Which data a table/chart reads, as the backend worded it (`sourceLabel` on a resolve response,
// `source_label` on a stored doc / dashboard visual / chart meta), else null.
export function sourceLabelFor(tableOrVisual) {
  const t = tableOrVisual || {};
  const label = t.sourceLabel || t.source_label;
  return label ? String(label) : null;
}

// The "Which data does this table use?" dropdown: required while the backend couldn't decide
// (`sourceStatus: "ambiguous"`), and kept after the customer picks one so they can change it.
// Options come from the backend (`sourceOptions`); `chosen` is the picked category key.
export function sourceChoiceState(table) {
  const t = table || {};
  const options = (Array.isArray(t.sourceOptions) ? t.sourceOptions : [])
    .filter((o) => o && o.category)
    .map((o) => ({
      category: o.category,
      label: o.label || o.category,
      matched: o.matched,
      total: o.total,
      text: (o.total ? `${o.label || o.category} · matched ${o.matched || 0}/${o.total} columns` : (o.label || o.category)),
    }));
  const status = t.sourceStatus || t.source_status || null;
  const entry = t.sourceCategory || t.source_category || null;
  const chosen = t.source_choice || (status === "chosen" && entry && entry.category) || "";
  // A carried-over table whose stored pick still fits comes back "chosen" WITH the options, so
  // the dropdown stays visible and the customer can change it.
  const required = options.length > 0
    && (status === "ambiguous" || status === "chosen" || Boolean(t.source_choice));
  return { required, options, chosen };
}

// Dashboard: the per-(column, cause) "couldn't be calculated" notes for a pinned table visual.
export function uncalculableNotesForVisual(visual) {
  const list = visual && Array.isArray(visual.uncalculable_notes) ? visual.uncalculable_notes : [];
  return list.filter((n) => n && n.text).map((n) => ({
    severity: "warning", code: n.code || "TABLE_UNCALCULABLE", text: n.text,
    key: `${n.column_header || ""}|${n.cause || ""}|${n.range || ""}`,
  }));
}

function fmtNumber(v) {
  if (v === null || v === undefined || Number.isNaN(Number(v))) return null;
  const n = Number(v);
  return n.toLocaleString("en-US", { maximumFractionDigits: Math.abs(n) < 1 ? 4 : 2 });
}

// One differing cell, in words: `Sheet!C9 · row "2026-09-02" × "Sum of Impressions" · Excel 342 · ours 1,342`.
export function reconcileCellLine(cell) {
  const parts = [cell.sheet_address];
  if (cell.row_label && cell.column_header) parts.push(`row "${cell.row_label}" × "${cell.column_header}"`);
  else if (cell.row_label) parts.push(`row "${cell.row_label}"`);
  else if (cell.column_header) parts.push(`column "${cell.column_header}"`);
  const excel = fmtNumber(cell.excel_value);
  if (excel !== null) parts.push(`Excel ${excel}`);
  const live = fmtNumber(cell.live_value);
  parts.push(live !== null ? `ours ${live}` : "ours: couldn't be calculated");
  return parts.join(" · ");
}

// The dashboard note for a pinned-table visual (`reconcile_note` from the backend), or null.
export function reconcileNoteForVisual(visual) {
  const n = visual && visual.reconcile_note;
  if (!n || !n.text) return null;
  return { severity: n.severity === "warning" ? "warning" : "info", code: n.code || null, text: n.text };
}

// Charts whose Excel filter can't be applied (`filter_warnings`) need an explicit per-chart
// "publish without that filter" confirmation; excluded and duplicate charts don't count.
export function chartFilterGate(visualsMeta, ackSet, excludedSet) {
  const acks = ackSet || new Set();
  const excluded = excludedSet || new Set();
  const flagged = (visualsMeta || [])
    .filter((vm) => vm && vm.status !== "duplicate" && !excluded.has(vm.chartId)
      && Array.isArray(vm.filter_warnings) && vm.filter_warnings.length > 0)
    .map((vm) => vm.chartId);
  const unacked = flagged.filter((id) => !acks.has(id));
  return { flagged, unacked, canPublish: unacked.length === 0 };
}
